<template>
  <div class="tw-flex tw-items-center tw-gap-2">
    <div
      v-if="showCurrentQueryBanner"
      class="
        tw-flex
        tw-gap-1
        tw-items-center
        tw-rounded-lg
        tw-text-sf-secondary-light
        tw-bg-sf-slate-lighter-1/50
        tw-ml-2
        tw-px-2
        tw-py-1.5
        tw-font-medium
        inline-alert-banner
      "
    >
      <div
        class="
          tw-uppercase
          tw-text
          tw-text-sf-secondary-lighter
          tw-text-xs
          tw-mr-1
          tw-text-nowrap
        "
      >
        {{ I18n.t('companies_overview.progress_v2.current_query') }}
      </div>
      <div v-if="segment" class="tw-font-medium tw-line-clamp-1">
        {{ segment.name }} <SegmentIcon :segment="segment" />
      </div>
      <div v-else class="tw-font-normal">
        {{ I18n.t('segments.modal.all_companies') }}
      </div>

      <SfButtonDs
        size="sm"
        class="tw-ml-1"
        data-testid="select-segment-btn"
        @click="showSegmentModal"
      >
        {{ I18n.t('companies_overview.progress_v2.select_another') }}
      </SfButtonDs>
    </div>
    <UnsavedChangesAlert />
    <SfBannerInline
      v-if="!runStartFailed"
      variant="neutral"
      :show-icon="false"
      single-line
      class="
        sf-banner--no-border
        !tw-bg-sf-slate-lighter-1/50 !tw-text-sf-dark-dark
        tw-min-w-32 tw-overflow-hidden
      "
      :class="{
        '[&_.sf-font-bold]:tw-font-normal': showSegmentReadyConfirmation,
      }"
      data-testid="results-alert"
    >
      <template #title>
        <!-- One live region kept mounted across the whole loading lifecycle:
             screen readers only announce updates to regions already in the
             DOM, so the busy flag and the message must swap in place. -->
        <span
          role="status"
          :aria-busy="isLoading ? 'true' : 'false'"
          class="tw-flex tw-items-center tw-min-w-20 tw-overflow-hidden"
        >
          <!-- Loading message -->
          <span
            v-if="isLoading"
            class="
              tw-flex
              tw-items-center
              tw-min-w-0
              tw-overflow-hidden
              tw-text-nowrap
              tw-tabular-nums
            "
            data-testid="loading-message"
          >
            <i
              aria-hidden="true"
              class="
                fa-solid fa-circle-notch fa-spin
                tw-text-sf-info-light-2 tw-mr-2 tw-text-[13px] tw-shrink-0
              "
            />
            <!-- Flex renders the message's text and its bold span as separate
                 items with an exact 4px gap between them. -->
            <span v-dompurify-html="loadingMessage" class="tw-flex tw-gap-1" />
          </span>

          <!-- Number of results message. Focusable whenever it carries the
               detail tooltip, so keyboard users can reveal the full text
               once the visible label truncates. -->
          <span
            v-else
            v-dompurify-html="resultsMessage"
            v-tooltip.placementBottom="resultsTooltip"
            :tabindex="resultsTooltip ? 0 : undefined"
            data-testid="number-of-results-message"
            data-tooltip-size="sm"
            class="tw-block tw-min-w-0 tw-truncate tw-tabular-nums"
          />
        </span>
      </template>

      <template v-if="hasResultsActions" #actions>
        <!-- Segment-ready opt-in button, shown until the query (including
             slow filters) is fully loaded, so the user can still opt in
             after the fast results come back. Once armed, the button is
             replaced by the "Will notify" confirmation below. -->
        <SfButtonDs
          v-if="showSegmentReadyOptIn"
          v-tooltip.once.placementBottom="
            I18n.t('companies_overview.segment_ready.button_tooltip')
          "
          size="sm"
          class="tw-shrink-0"
          data-testid="segment-ready-btn"
          data-tooltip-size="sm"
          @click="requestSegmentReady"
        >
          {{ I18n.t('companies_overview.segment_ready.button') }}
        </SfButtonDs>
        <span
          v-else-if="showSegmentReadyConfirmation"
          v-tooltip.placementBottom="
            I18n.t('companies_overview.segment_ready.tooltip')
          "
          class="tw-font-bold tw-shrink-0"
          data-testid="segment-ready-confirmation"
          data-tooltip-size="sm"
        >
          {{ I18n.t('companies_overview.segment_ready.confirmation') }}
        </span>

        <!-- Refresh data button -->
        <SfButtonDs
          v-if="canRefreshSegmentData"
          size="sm"
          class="tw-shrink-0"
          data-testid="refresh-data-btn"
          @click="refreshData"
        >
          {{ I18n.t('companies_overview.progress_v2.refresh') }}
        </SfButtonDs>
      </template>
    </SfBannerInline>

    <InsightsActions
      v-if="queryOverviewEnabled"
      drawer-direction="left"
      data-testid="toolbar-actions"
      @download-as-excel="downloadCompaniesList"
    />

    <!-- The wrapper carries the tooltip: a disabled button swallows the
         pointer events the directive needs. -->
    <span
      v-if="showManageTriggersButton"
      v-tooltip.placementBottom="manageTriggersTooltip"
      class="tw-inline-flex tw-shrink-0"
      data-tooltip-size="sm"
      data-testid="toolbar-manage-triggers"
    >
      <SfButtonDs
        variant="secondary"
        size="lg"
        icon-only
        icon-before="paper-plane"
        :disabled="manageTriggersDisabled"
        :aria-label="I18n.t('companies_overview.manage_triggers.title')"
        data-testid="toolbar-manage-triggers-btn"
        @click="openManageTriggersModal"
      />
    </span>

    <SfButtonDs
      v-if="showAssistantButton"
      variant="secondary"
      size="lg"
      icon-only
      icon-before="wand-magic-sparkles"
      :aria-label="I18n.t('companies_overview.progress_v2.open_assistant')"
      data-testid="open-assistant-btn"
      @click="openAssistant"
    />
  </div>
</template>
<script>
import { useDrawer } from '@app/shared/pinia/drawer'
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia'
import { useCompaniesStore } from '@app/insights/store/companies'
import { useColumnsStore } from '@app/insights/store/columns'
import { useSegmentsStore } from '@app/insights/store/segments'
import { useTriggersStore } from '@app/insights/store/triggers'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import { SfTime } from '@app/adapters/time'
import { SfLog } from '@app/adapters/logger'
import { capitalize } from '@app/utils/string-helpers'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { isFeatureFlagEnabled } from '@app/adapters/silverfin'
import SegmentIcon from '../segments/segment-icon.vue'
import InsightsActions from '../actions/insights-actions.vue'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import UnsavedChangesAlert from './unsaved-changes-alert.vue'
import { downloadCompaniesExcel } from '@app/insights/utils/companies-excel-export'
import {
  isSegmentReadyArmed,
  armSegmentReady,
  setActiveInsightsSegmentId,
  isOnInsights,
} from '@app/insights/utils/segment-ready'

export default {
  name: 'InsightsCompaniesListStatus',
  components: {
    SegmentIcon,
    InsightsActions,
    SfBannerInline,
    SfButtonDs,
    UnsavedChangesAlert,
  },
  // InsightsActions renders in this app now, and its dropdown reads isLite
  // through an injection.
  provide() {
    return {
      isLite: this.isLite,
    }
  },
  props: {
    isLite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      segmentReadyArmed: false,
      // Flipped while a user-initiated Refresh is in flight. Refresh
      // forces `noCache: true`, which destroys the cached SegmentRun
      // and re-runs from scratch — so the alert-when-ready button is
      // useful again, even though `hasCachedSegmentRun` is still true
      // (the segment payload's `last_request_ref` doesn't get updated
      // until the new run finishes and `silentReloadCurrentSegment`
      // re-fetches it).
      manualRefreshInFlight: false,
    }
  },
  computed: {
    ...mapPiniaState(useCompaniesStore, [
      'companiesFullyLoaded',
      'companiesFullyLoadedAt',
      'searchTerm',
      'selectedCompanies',
      'companiesProcessedPercentage',
      'allCompaniesProcessed',
      'sortedAndFilteredCompanies',
      'isSegmentModified',
      'runStartFailed',
    ]),
    ...mapPiniaState(useSegmentsStore, ['segment', 'isSegmentLoading']),
    ...mapPiniaState(useTriggersStore, ['manageTrigger']),
    ...mapPiniaState(useColumnsStore, ['companyColumns']),
    // The companies store's own count is the post-SQL-filter result set;
    // the denominator shown to the user is the number of clients they can
    // access, which only the root store knows.
    ...mapPiniaState(useInsightsRootStore, {
      accessibleCompaniesCount: 'allCompaniesCount',
      queryOverviewEnabled: 'queryOverviewEnabled',
    }),
    ...mapPiniaState(useDrawer, ['isLeftDrawerOpen', 'rightDrawerContent']),

    showAssistantButton() {
      return (
        isFeatureFlagEnabled('collab_insights_assistant') &&
        this.rightDrawerContent !== RightDrawerContent.InsightsAssistant
      )
    },

    // Only allow opting in to the completion alert when the user is
    // looking at a saved segment (not ad-hoc / unsaved filters), the
    // segment hasn't been modified, and the BE has returned the segment
    // payload — without the `isSegmentLoading` gate, `segment.value` is
    // briefly the bare `{ id }` from `loadSegment` and `last_request_ref`
    // is undefined, so the button would flash on and immediately back
    // off once the cached payload lands.
    canUseSegmentReady() {
      return !!this.segment && !this.isSegmentModified && !this.isSegmentLoading
    },
    // True when the segment payload reports a *finished* SegmentRun
    // (set by _segment.json.jbuilder). Results are then served from
    // cache and arrive within a few hundred ms — no point offering an
    // "alert me later" button that would just flicker on and back off.
    // An in-progress SegmentRun (e.g. the user landed mid-run, or is
    // waiting on slow filters) leaves this false so the button still
    // shows.
    hasFinishedSegmentRun() {
      return !!this.segment?.last_segment_run_finished
    },
    showSegmentReadyOptIn() {
      return (
        this.canUseSegmentReady &&
        !this.companiesFullyLoaded &&
        (!this.hasFinishedSegmentRun || this.manualRefreshInFlight) &&
        !this.segmentReadyArmed
      )
    },
    showSegmentReadyConfirmation() {
      return (
        this.canUseSegmentReady &&
        !this.companiesFullyLoaded &&
        (!this.hasFinishedSegmentRun || this.manualRefreshInFlight) &&
        this.segmentReadyArmed
      )
    },

    // SfBannerInline keys its has-actions layout off slot presence, not slot
    // content, so an unconditional #actions template claims it even when empty.
    hasResultsActions() {
      return (
        this.showSegmentReadyOptIn ||
        this.showSegmentReadyConfirmation ||
        this.canRefreshSegmentData
      )
    },

    loading() {
      return !this.allCompaniesProcessed
    },
    isLoading() {
      return this.loading || this.isSegmentLoading
    },
    loadingMessage() {
      return I18n.t('companies_overview.progress_v2.loading_html', {
        percentage: this.companiesProcessedPercentage,
      })
    },

    resultsMessage() {
      if (this.resultCompaniesSize === 0) {
        return I18n.t('companies_overview.progress_v2.no_clients_files_matched')
      }

      const count = I18n.t(
        'companies_overview.progress_v2.n_clients',
        this.resultMessageParams
      )

      // The suffix goes through its own key rather than being concatenated
      // on, so translators keep control of word order and separator.
      if (!this.companiesFullyLoaded) {
        return I18n.t(
          'companies_overview.progress_v2.count_with_loading_html',
          {
            count,
            loading: I18n.t(
              'companies_overview.progress_v2.loading_client_data'
            ),
          }
        )
      }

      if (!this.cachedResultsAge) {
        return count
      }

      return I18n.t('companies_overview.progress_v2.count_with_age_html', {
        count,
        age: this.cachedResultsAge,
      })
    },
    resultsTooltip() {
      if (this.resultCompaniesSize === 0) {
        return ''
      }

      // The tooltip carries two independent counts, so each fragment is
      // pluralized on its own raw count; the display values stay
      // separately formatted strings.
      const filtered = I18n.t(
        'companies_overview.progress_v2.count_tooltip_filtered',
        {
          resultCompaniesSize: this.resultMessageParams.resultCompaniesSize,
          count: this.resultCompaniesSize,
        }
      )
      const accessible = I18n.t(
        'companies_overview.progress_v2.count_tooltip_accessible',
        {
          accessibleCompaniesCount:
            this.resultMessageParams.accessibleCompaniesCount,
          count: this.accessibleCompaniesCount ?? 0,
        }
      )

      return I18n.t('companies_overview.progress_v2.count_tooltip', {
        filtered,
        accessible,
      })
    },
    // Constructing a formatter is the expensive part of Intl, so keep it out
    // of resultMessageParams — that recomputes on every streamed chunk.
    numberFormat() {
      return new Intl.NumberFormat(window.Silverfin?.currentUserLocale?.())
    },
    resultMessageParams() {
      return {
        resultCompaniesSize: this.numberFormat.format(this.resultCompaniesSize),
        accessibleCompaniesCount: this.numberFormat.format(
          this.accessibleCompaniesCount ?? 0
        ),
      }
    },
    resultCompaniesSize() {
      return this.sortedAndFilteredCompanies.length
    },
    cachedResultsAge() {
      if (!this.companiesFullyLoadedAt) {
        return null
      }

      return capitalize(SfTime.unix(this.companiesFullyLoadedAt).fromNow())
    },
    canRefreshSegmentData() {
      // `loading` only tracks fast-filter chunks (allCompaniesProcessed);
      // for 2-step segments slow filters keep `companiesFullyLoaded`
      // false past that point, and `cachedResultsAge` may still hold the
      // previous run's timestamp. Gate on the full-load flag so the
      // button only appears once everything has settled.
      return (
        this.companiesFullyLoaded &&
        (this.cachedResultsAge || this.resultCompaniesSize === 0)
      )
    },
    searchPlaceholder() {
      return I18n.t('companies_overview.progress_v2.search_placeholder')
    },
    showCurrentQueryBanner() {
      return !this.queryOverviewEnabled && !this.isLeftDrawerOpen
    },

    // Lite firms only get the premium teaser in the left panel; manageTrigger
    // narrows the button to the users allowed to create triggers.
    showManageTriggersButton() {
      return this.queryOverviewEnabled && !this.isLite && this.manageTrigger
    },
    // Recommended queries are read-only; triggers need a firm-owned copy.
    // Read the segment state, not the URL: this app has no router, and the
    // drawer app rewrites the URL only after the segment has changed, so a
    // URL read here caches a stale partner_segment_id.
    isPartnerSegment() {
      const partnerSegmentId = this.segment?.partner_segment_id
      const recommended = this.segment?.recommended

      return Boolean(partnerSegmentId) || Boolean(recommended)
    },
    // Filters that need a signed-in user cannot run in a scheduled trigger.
    triggerIncompatibilityReasons() {
      return this.segment?.incompatibility_reasons || []
    },
    manageTriggersDisabled() {
      return (
        !this.segment ||
        this.isSegmentLoading ||
        this.isSegmentModified ||
        this.isPartnerSegment ||
        this.triggerIncompatibilityReasons.length > 0
      )
    },
    // Same precedence as the left panel's addTriggerTooltip: an unsaved
    // modification is what the user has to resolve first, whatever else the
    // query would also be blocked on.
    manageTriggersTooltip() {
      if (!this.segment || this.isSegmentModified) {
        return I18n.t(
          'companies_overview.save_query_to_enable_trigger_creation'
        )
      }

      if (this.isPartnerSegment) {
        return I18n.t(
          'companies_overview.copy_recommended_query_to_enable_trigger_creation'
        )
      }

      if (this.triggerIncompatibilityReasons.length) {
        return this.triggerIncompatibilityReasons
          .map((reason) =>
            // Only some reasons carry a creation-specific wording; the rest
            // fall back to the generic one instead of a missing translation.
            I18n.t(
              `insight_triggers.incompatibility.${reason}_creation_disabled`,
              {
                defaults: [
                  { scope: `insight_triggers.incompatibility.${reason}` },
                ],
              }
            )
          )
          .join(' ')
      }

      if (this.manageTriggersDisabled) {
        return I18n.t(
          'companies_overview.save_query_to_enable_trigger_creation'
        )
      }

      return I18n.t('companies_overview.manage_triggers.title')
    },
  },
  watch: {
    segment: {
      immediate: true,
      handler(newSegment) {
        // Sync the local UI toggle with whatever is currently armed in
        // localStorage for *this* segment (or the all-clients run when
        // newSegment is null). Runs on initial mount and whenever the
        // user switches segment.
        this.segmentReadyArmed = isSegmentReadyArmed(
          window.Silverfin?.currentFirmId,
          newSegment?.id ?? null
        )
        // Tell the global toast gate which segment is active so it can
        // suppress redundant segment-ready toasts for the segment the
        // user is already viewing.
        setActiveInsightsSegmentId(newSegment?.id ?? null)
      },
    },
    companiesFullyLoaded(done) {
      if (done) {
        // Query finished — the global subscriber has already disarmed the
        // flag when the toast fired (or the user never opted in); sync
        // the local UI so the button is re-offered for the next run.
        this.segmentReadyArmed = false
        this.manualRefreshInFlight = false
      }
    },
  },
  mounted() {
    document.addEventListener('insights:open-segment', this.handleOpenSegment)
  },
  beforeUnmount() {
    document.removeEventListener(
      'insights:open-segment',
      this.handleOpenSegment
    )
    setActiveInsightsSegmentId(null)
  },
  methods: {
    ...mapPiniaActions(useCompaniesStore, ['fetchFilteredCompanies']),
    ...mapPiniaActions(useSegmentsStore, ['showSegmentModal', 'loadSegment']),
    ...mapPiniaActions(useDrawer, ['openRightDrawer']),
    openAssistant() {
      // openRightDrawer no-ops when the assistant is already showing, so the
      // skip-persist flag cannot stick and swallow the next genuine persist.
      this.openRightDrawer(RightDrawerContent.InsightsAssistant, true)
    },
    // Toast click → in-place segment switch. Calling preventDefault is
    // the signal the toast manager uses to know the click was handled
    // (so it dismisses the toast and skips navigation). If we're not on
    // the companies overview page we don't intercept and the toast
    // link's href triggers a full-page navigation as usual.
    handleOpenSegment(event) {
      if (!isOnInsights()) return

      const detail = event.detail ?? {}
      const segmentId = parseInt(detail.segmentId, 10)

      if (!Number.isFinite(segmentId)) return

      const partnerSegmentId = detail.partnerSegmentId
        ? parseInt(detail.partnerSegmentId, 10)
        : null

      event.preventDefault()
      this.loadSegment({ id: segmentId, partner_segment_id: partnerSegmentId })
    },
    openManageTriggersModal() {
      if (this.manageTriggersDisabled) return
      window.modalCitizen.openModalPortal('insights-manage-triggers-modal')
    },
    downloadCompaniesList() {
      downloadCompaniesExcel({
        companyColumns: this.companyColumns,
        sortedAndFilteredCompanies: this.sortedAndFilteredCompanies,
        selectedCompanies: this.selectedCompanies,
      })
    },
    requestSegmentReady() {
      const firmId = window.Silverfin?.currentFirmId
      if (!firmId) return
      armSegmentReady(firmId, this.segment?.id ?? null)
      this.segmentReadyArmed = true
    },
    async refreshData() {
      const searchParams = new URLSearchParams(window.location.search)
      const requestRef = searchParams.get('requestRef')

      // noCache=true on the BE destroys the cached SegmentRun and starts
      // a fresh run, so the alert-when-ready button is meaningful again.
      this.manualRefreshInFlight = true

      try {
        await this.fetchFilteredCompanies({
          existingRequestRef: requestRef,
          noCache: true,
        })
      } catch (e) {
        this.manualRefreshInFlight = false
        SfLog.warn('Error requesting Insights companies when refreshing', {
          extra: {
            firmId: window.Silverfin.currentFirmId,
            error: e,
          },
        })
      }
    },
  },
}
</script>
